export type InventoryStatus = 'ready' | 'loading' | 'error'
export interface Fulfillment {
  warehouseId: number
  logisticsType: ProductV3.ILogisticsType
}

/** Keep the existing single-warehouse FBK -> GS -> DS policy. */
export const resolveFulfillment = (inventory: ProductV3.Inventory | undefined, quantity: number): Fulfillment | undefined => {
  if (!inventory || !Number.isFinite(quantity) || quantity < 1) return
  if (inventory.fbkAvailable > 0) {
    const warehouse = inventory.fbkInventories?.find(item => item.available >= quantity)
    return warehouse?.warehouseId ? { warehouseId: warehouse.warehouseId, logisticsType: 'fbk' } : undefined
  }
  if (inventory.gsAvailable >= quantity && inventory.gsWarehouseId) return { warehouseId: inventory.gsWarehouseId, logisticsType: 'gs' }
  if (inventory.dsAvailable >= quantity && inventory.dsWarehouseId) return { warehouseId: inventory.dsWarehouseId, logisticsType: 'ds' }
}

export const activityKey = (activity?: ProductV3.SelectedSkuActivity | null): string => activity
  ? [activity.skuId, activity.activityType, activity.activityId, activity.endTime].join(':') : ''

export const activityHasEnded = (activity?: ProductV3.SelectedSkuActivity | null): boolean => !!activity
  && [1, 4, 6].includes(activity.activityType)
  && new Date(activity.endTime).getTime() <= new Date(activity.queryTime).getTime()

/** Keep opaque attribution values; never carry flash-sale routing into the ordinary page. */
export const preserveOriginalAttribution = <T extends { url: string, cookie: { query: Record<string, unknown> } }>(target: T, query: Record<string, unknown>): T => {
  const preserved: Record<string, string> = {}
  for (const key of ['cpsShareAsset', 'sourceMeta', 'authorCode', 'referCode', 'articleId', 'isAd', 'isNewTag', 'isChoiceTag', 'requestId', 'algorithmModelName']) {
    if (typeof query[key] === 'string') preserved[key] = query[key] as string
  }
  const search = new URLSearchParams(preserved).toString()
  return { ...target, url: target.url + (search ? `${target.url.includes('?') ? '&' : '?'}${search}` : ''), cookie: { ...target.cookie, query: { ...target.cookie.query, ...preserved } } }
}

export const activeInventory = (sku?: ProductV3.Sku, activity?: ProductV3.SelectedSkuActivity | null) =>
  activity?.activityType === 1 && activity.flashSale?.status === 1 ? activity.flashSale.inventory : sku?.inventory

export interface PurchaseActions {
  buy: 'buy' | 'original-price' | 'blocked'
  cart: boolean
  reason: 'ready' | 'loading' | 'error' | 'sku' | 'stock' | 'store' | 'activity'
}

/** The action is based on inventory, never the delivery-time response. */
export const purchaseActions = (options: {
  sku?: ProductV3.Sku
  activity?: ProductV3.SelectedSkuActivity | null
  store?: { status: number }
  quantity: number
  inventoryStatus?: InventoryStatus
  platform: 'pc' | 'wap'
  allowOriginalPrice?: boolean
}): PurchaseActions => {
  const { sku, activity, store, quantity, platform } = options
  const blocked = (reason: PurchaseActions['reason']): PurchaseActions => ({ buy: 'blocked', cart: false, reason })
  if (options.inventoryStatus === 'loading' || !store) return blocked('loading')
  if (options.inventoryStatus === 'error') return blocked('error')
  if (!sku || sku.status < 0) return blocked('sku')
  if (activity && activity.skuId !== sku.skuId) return blocked('activity')
  const normal = resolveFulfillment(sku.inventory, quantity)
  const allowed = (plan?: Fulfillment) => !!plan && (store.status !== 2 || plan.logisticsType === 'fbk')
  if (activity?.activityType !== 1) {
    const canBuy = allowed(normal)
    return canBuy ? { buy: 'buy', cart: true, reason: 'ready' } : blocked(store.status === 2 ? 'store' : 'stock')
  }
  if (activity.flashSale?.status !== 1) return blocked('activity')
  const flash = resolveFulfillment(activity.flashSale.inventory, quantity)
  const canBuy = allowed(flash)
  // Preserve the existing platform guards. Both cart payloads still submit activityId/type = 0.
  const cart = platform === 'pc' ? allowed(normal) && (store.status !== 2 || canBuy) : canBuy
  if (canBuy) return { buy: 'buy', cart, reason: 'ready' }
  const stock = activity.flashSale.inventory
  const soldOut = Math.max(stock.fbkAvailable, stock.gsAvailable, stock.dsAvailable) <= 0
  // Original-price navigation lets the buyer select quantity again on the ordinary page.
  const normalHasStock = Math.max(sku.inventory.fbkAvailable, sku.inventory.gsAvailable, sku.inventory.dsAvailable) > 0
  const original = options.allowOriginalPrice && soldOut && normalHasStock
    && Number.isFinite(quantity) && quantity >= 1 && store.status !== 2
  return { buy: original ? 'original-price' : 'blocked', cart, reason: store.status === 2 ? 'store' : 'stock' }
}
